import { createFileRoute, redirect } from '@tanstack/react-router'
import { requireAuthenticatedSession } from '@/lib/require-authenticated-session'

/**
 * `/files` is a resolution step, not a page: it forwards to the caller's
 * personal folder.
 *
 * The segment exists so the navigation has something stable to point at. A
 * sidebar entry cannot name the personal folder's id — the id differs per
 * person, and the shell renders before anything has read it.
 *
 * NO COMPONENT, and the redirect is in `beforeLoad` rather than in a loader
 * for that reason: a route with a component paints it for a frame before the
 * redirect takes effect, which reads as a flash of the wrong screen.
 *
 * **The id comes from the session, in no request of its own.** It rides on
 * `get-me`, which the session contract already reads at bootstrap, so this
 * route resolves from cache. It used to scan the first page of `list-shared-with-me`
 * for the row whose `personalOwnerId` was the caller's, which was the gap
 * OPSSTD-5582 reported and OPSSTD-5601 turned into a defect: that list is
 * paginated and ordered by name, the person may rename their folder to
 * anything, and enough shares above it put their own folder on page two —
 * where this found nothing and «I miei file» came back empty. The folder is
 * now out of that listing entirely, which is why nothing here scans it.
 *
 * **Never by name.** It is created with a localized one and is renameable
 * like any other, so a lookup keyed on the string breaks on first contact
 * with a real user.
 */
export const Route = createFileRoute('/(private)/(shell)/files/')({
	beforeLoad: async ({ context }) => {
		// Narrowed, not guarded: the gate on `(private)` already refused an
		// anonymous caller, and this is only what lets `user` be dereferenced
		// below.
		const session = await requireAuthenticatedSession(context.queryClient)
		const personalFolderId = session.user.personalFolderId

		// The folder exists from a person's first sign-in and this route sits
		// below the gate that proved they signed in, so a null here is our
		// defect and belongs on the error boundary rather than dressed up as
		// an empty folder.
		if (!personalFolderId) {
			throw new Error('The session carries no personal folder.')
		}

		throw redirect({
			to: '/files/$nodeId',
			params: { nodeId: personalFolderId }
		})
	}
})
